import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import $ from 'jquery';

export default class PdfDownloader {
    constructor(elm) {
        PdfDownloader.instances = PdfDownloader.instances || new Set();
        PdfDownloader.sharedState = PdfDownloader.sharedState || {
            preparing: false,
            isReady: false,
            pendingPrepare: null,
            cachedPdfBlob: null,
            cachedPdfUrl: null,
            owner: null,
        };

        this.$elm = $(elm);
        this.button = elm;
        this.element = document.querySelector('.questionnaire-embed-page');
        this.body = document.body;
        this.loading = false;
        this.pdfViewportWidth = 1024;
        this.pdfMargin = [10, 10, 10, 10];
        this.pdfFilename = 'uitkomst.pdf';
        this.pdfPageNumberHeightMm = 8;
        this.canvasScale = 1.25;
        this.imageQuality = 0.82;
        this.exportRootClass = 'pdf-export-root';
        this.accordionSectionClass = 'accordion-section';
        this.accordionToggleClass = 'accordion-section--isOpen';
        this.accordionContentClass = 'accordion-sectionContent';
        this.accordionImageClass = 'accordion-sectionImage';
        this.hideInPdfClass = 'hide-in-pdf';
        this.flattenedWidgetClass = 'pdf-widget-item';
        this.exportFlowClass = 'pdf-export-flow';
        this.pagedFlowClass = 'pdf-paged-flow';
        this.pageClass = 'pdf-page';
        this.pageContentClass = 'pdf-page-content';
        this.exportWidgetSelector = '.widget, .widget-questionnaire-score';
        this.pdfExcludedSelectors = [
            `.${this.hideInPdfClass}`,
            '.widget-inpagenavigation',
            '.widget-questionnaire-score .is-download',
        ];
        this.handleResultsReady = () => {
            window.setTimeout(() => {
                this.prepareDownload(true).catch((error) => {
                    console.error('PDF background preparation failed:', error);
                    PdfDownloader.instances.forEach((instance) => instance.setButtonState('idle'));
                });
            }, 50);
        };

        if (!this.element) return;

        this.init()
    }

    init() {
        PdfDownloader.instances.add(this);
        if (!PdfDownloader.sharedState.owner) {
            PdfDownloader.sharedState.owner = this;
            window.addEventListener('questionnaire:results', this.handleResultsReady);
        }

        this.setButtonState(PdfDownloader.sharedState.preparing ? 'preparing' : (PdfDownloader.sharedState.isReady ? 'ready' : 'idle'));
        this.$elm.on('click', (event) => this.download(event));
    }

    setButtonState(state) {
        const isPreparing = state === 'preparing';
        const isReady = state === 'ready';
        const isDisabled = state !== 'ready';

        this.button.classList.toggle('is-preparing', isPreparing);
        this.button.classList.toggle('is-ready', isReady);
        this.button.setAttribute('aria-disabled', String(isDisabled));
        this.button.tabIndex = isDisabled ? -1 : 0;
    }

    invalidateCachedPdf() {
        if (PdfDownloader.sharedState.cachedPdfUrl) {
            URL.revokeObjectURL(PdfDownloader.sharedState.cachedPdfUrl);
        }

        PdfDownloader.sharedState.cachedPdfBlob = null;
        PdfDownloader.sharedState.cachedPdfUrl = null;
        PdfDownloader.sharedState.isReady = false;
    }

    captureAccordionState() {
        const $sections = this.element.querySelectorAll(`.${this.accordionSectionClass}`);

        return Array.from($sections).map($section => {
            const $content = $section.querySelector(`.${this.accordionContentClass}`);
            const imageId = $section.dataset.image;
            const $image = imageId ? document.getElementById(imageId) : null;

            return {
                $section,
                $content,
                $image,
                isOpen: $section.classList.contains(this.accordionToggleClass),
                contentStyle: $content ? $content.getAttribute('style') : null,
                imageIsActive: $image ? $image.classList.contains('isActive') : null,
            };
        });
    }

    expandAccordions(accordionState) {
        accordionState.forEach(({ $section, $content, $image }) => {
            $section.classList.add(this.accordionToggleClass);

            if ($content) {
                $content.style.display = 'block';
            }

            if ($image && $image.classList.contains(this.accordionImageClass)) {
                $image.classList.add('isActive');
            }
        });
    }

    restoreAccordions(accordionState) {
        accordionState.forEach(({ $section, $content, $image, isOpen, contentStyle, imageIsActive }) => {
            $section.classList.toggle(this.accordionToggleClass, isOpen);

            if ($content) {
                if (contentStyle === null) {
                    $content.removeAttribute('style');
                } else {
                    $content.setAttribute('style', contentStyle);
                }
            }

            if ($image && imageIsActive !== null) {
                $image.classList.toggle('isActive', imageIsActive);
            }
        });
    }

    hidePdfExcludedElements(clone) {
        const excludedElements = clone.querySelectorAll(this.pdfExcludedSelectors.join(', '));

        excludedElements.forEach(($element) => {
            $element.remove();
        });
    }

    expandAccordionsInClone(clone) {
        const sections = clone.querySelectorAll(`.${this.accordionSectionClass}`);

        sections.forEach(($section) => {
            $section.classList.add(this.accordionToggleClass);

            const content = $section.querySelector(`.${this.accordionContentClass}`);

            if (content) {
                content.style.display = 'block';
            }

            const imageId = $section.dataset.image;

            if (!imageId) {
                return;
            }

            const image = clone.querySelector(`#${CSS.escape(imageId)}`);

            if (image && image.classList.contains(this.accordionImageClass)) {
                image.classList.add('isActive');
            }
        });
    }

    normalizePersonImagesInClone(clone) {
        const personImageContainers = clone.querySelectorAll('.person-image-container');

        personImageContainers.forEach(($container) => {
            const existingImage = $container.querySelector('img');

            if (!existingImage) {
                return;
            }

            const normalizedImage = document.createElement('img');
            normalizedImage.className = existingImage.className || 'person-image';
            normalizedImage.alt = existingImage.getAttribute('alt') || '';
            normalizedImage.loading = 'eager';
            normalizedImage.decoding = 'sync';
            normalizedImage.src = existingImage.currentSrc || existingImage.getAttribute('src') || '';

            if (!normalizedImage.src) {
                return;
            }

            $container.classList.add('person-image-container--pdf-fill');
            $container.style.backgroundImage = `url("${normalizedImage.src}")`;
            $container.replaceChildren();
        });
    }

    async waitForCloneRender(clone) {
        const images = Array.from(clone.querySelectorAll('img'));
        const imageWaitTimeoutMs = 1500;

        await Promise.all(images.map(($image) => {
            if ($image.complete) {
                return Promise.resolve();
            }

            return new Promise((resolve) => {
                const finish = () => {
                    clearTimeout(timeoutId);
                    resolve();
                };

                const timeoutId = window.setTimeout(finish, imageWaitTimeoutMs);
                $image.addEventListener('load', finish, { once: true });
                $image.addEventListener('error', finish, { once: true });
            });
        }));

        if (document.fonts?.ready) {
            await Promise.race([
                document.fonts.ready,
                new Promise((resolve) => window.setTimeout(resolve, imageWaitTimeoutMs)),
            ]);
        }

        await this.waitForLayout();
    }

    async waitForLayout() {
        await new Promise((resolve) => requestAnimationFrame(resolve));
    }

    getPdfDimensionsMm() {
        const pageWidth = 215.9;
        const pageHeight = 279.4;
        const [marginTop, marginRight, marginBottom, marginLeft] = this.pdfMargin;
        const innerWidth = pageWidth - marginLeft - marginRight;
        const innerHeight = pageHeight - marginTop - marginBottom;

        return {
            pageWidth,
            pageHeight,
            marginTop,
            marginRight,
            marginBottom,
            marginLeft,
            innerWidth,
            innerHeight,
            contentHeight: innerHeight - this.pdfPageNumberHeightMm,
        };
    }

    getPdfPageHeightPx(containerWidthPx) {
        const { innerWidth, contentHeight } = this.getPdfDimensionsMm();

        return containerWidthPx * (contentHeight / innerWidth);
    }

    wrapAsPdfWidgetItem($element, id = '') {
        const wrapper = document.createElement('div');
        wrapper.className = this.flattenedWidgetClass;

        if (id) {
            wrapper.id = id;
        }

        wrapper.appendChild($element);
        return wrapper;
    }

    isVisibleElement($element) {
        if (!$element || $element.classList.contains('hide')) {
            return false;
        }

        const style = window.getComputedStyle($element);

        return style.display !== 'none' && style.visibility !== 'hidden';
    }

    isTopLevelExportWidget($element) {
        if (!$element.matches(this.exportWidgetSelector)) {
            return false;
        }

        const parentWidget = $element.parentElement?.closest(this.exportWidgetSelector);

        return !parentWidget;
    }

    createAccordionExportBlock($widget, $section, sectionIndex) {
        const widgetClone = $widget.cloneNode(true);
        const accordion = widgetClone.querySelector('.accordion');
        const sections = accordion ? Array.from(accordion.querySelectorAll(`.${this.accordionSectionClass}`)) : [];
        const matchingSection = sections[sectionIndex];

        if (!accordion || !matchingSection) {
            return widgetClone;
        }

        sections.forEach(($accordionSection, index) => {
            if (index !== sectionIndex) {
                $accordionSection.remove();
            }
        });

        const imageWrapper = widgetClone.querySelector('.accordion-sectionImageWrapper');

        if (imageWrapper) {
            const images = Array.from(imageWrapper.querySelectorAll(`.${this.accordionImageClass}`));

            images.forEach(($image, index) => {
                if (index !== sectionIndex) {
                    $image.remove();
                    return;
                }

                $image.classList.add('isActive');
            });
        }

        if (sectionIndex > 0) {
            widgetClone.querySelectorAll('.widget-heading, .widget-text, .widget-header').forEach(($element) => {
                $element.remove();
            });
        }

        return widgetClone;
    }

    appendWidgetExportBlocks(exportBlocks, $widget) {
        const accordionSections = Array.from($widget.querySelectorAll(`.${this.accordionSectionClass}`));

        if (accordionSections.length > 0) {
            accordionSections.forEach(($section, sectionIndex) => {
                const block = this.createAccordionExportBlock($widget, $section, sectionIndex);
                const blockId = sectionIndex === 0 ? ($widget.id || '') : '';
                exportBlocks.push(this.wrapAsPdfWidgetItem(block, blockId));
            });
            return;
        }

        exportBlocks.push(this.wrapAsPdfWidgetItem($widget.cloneNode(true), $widget.id || ''));
    }

    buildExportFlow(clone) {
        const exportFlow = document.createElement('div');
        exportFlow.className = this.exportFlowClass;
        const exportBlocks = [];

        const walker = document.createTreeWalker(clone, NodeFilter.SHOW_ELEMENT);
        let node = walker.currentNode;

        while (node) {
            const $element = node;

            if (
                $element.classList?.contains('pageintro-container') &&
                !exportBlocks.some(($block) => $block.id === 'pdf-pageintro')
            ) {
                exportBlocks.push(this.wrapAsPdfWidgetItem($element.cloneNode(true), 'pdf-pageintro'));
                node = walker.nextNode();
                continue;
            }

            if (this.isTopLevelExportWidget($element) && this.isVisibleElement($element)) {
                this.appendWidgetExportBlocks(exportBlocks, $element);
            }

            node = walker.nextNode();
        }

        exportFlow.append(...exportBlocks);
        clone.replaceChildren(exportFlow);
    }

    createPdfPage() {
        const page = document.createElement('section');
        page.className = this.pageClass;

        const content = document.createElement('div');
        content.className = this.pageContentClass;
        page.appendChild(content);

        return { page, content };
    }

    paginateExportFlow(clone) {
        const exportFlow = clone.querySelector(`.${this.exportFlowClass}`);

        if (!exportFlow) {
            return;
        }

        const items = Array.from(exportFlow.children);
        const pageHeightPx = this.getPdfPageHeightPx(clone.getBoundingClientRect().width);
        const pagedFlow = document.createElement('div');
        pagedFlow.className = this.pagedFlowClass;

        let { page, content } = this.createPdfPage();
        let currentHeight = 0;

        items.forEach(($item) => {
            const itemHeight = $item.getBoundingClientRect().height;
            const hasContent = content.childElementCount > 0;
            const fitsCurrentPage = currentHeight + itemHeight <= pageHeightPx;

            if (hasContent && !fitsCurrentPage) {
                pagedFlow.appendChild(page);
                ({ page, content } = this.createPdfPage());
                currentHeight = 0;
            }

            content.appendChild($item);
            currentHeight += itemHeight;
        });

        if (content.childElementCount > 0) {
            pagedFlow.appendChild(page);
        }

        exportFlow.replaceWith(pagedFlow);
    }

    async renderPdf(clone) {
        const pages = Array.from(clone.querySelectorAll(`.${this.pageClass}`));
        const dimensions = this.getPdfDimensionsMm();
        const pdf = new jsPDF({
            unit: 'mm',
            format: 'letter',
            orientation: 'portrait',
            compress: true,
        });

        for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
            const $page = pages[pageIndex];
            const canvas = await html2canvas($page, {
                backgroundColor: '#ffffff',
                scale: this.canvasScale,
                useCORS: true,
                logging: false,
                windowWidth: this.pdfViewportWidth,
            });

            const pxToMm = dimensions.innerWidth / canvas.width;
            const pageSliceHeightPx = Math.max(1, Math.floor(dimensions.contentHeight / pxToMm));
            const fitsSinglePdfPage = canvas.height <= pageSliceHeightPx + 2;

            if (pageIndex > 0) {
                pdf.addPage();
            }

            if (fitsSinglePdfPage) {
                pdf.addImage(
                    canvas.toDataURL('image/jpeg', this.imageQuality),
                    'JPEG',
                    dimensions.marginLeft,
                    dimensions.marginTop,
                    dimensions.innerWidth,
                    canvas.height * pxToMm,
                );
                continue;
            }

            let sourceY = 0;
            let isFirstSlice = true;

            while (sourceY < canvas.height) {
                if (!isFirstSlice) {
                    pdf.addPage();
                }

                const sliceHeightPx = Math.min(pageSliceHeightPx, canvas.height - sourceY);
                const sliceCanvas = document.createElement('canvas');
                sliceCanvas.width = canvas.width;
                sliceCanvas.height = sliceHeightPx;

                const sliceContext = sliceCanvas.getContext('2d');

                if (!sliceContext) {
                    throw new Error('Could not prepare PDF canvas slice.');
                }

                sliceContext.drawImage(
                    canvas,
                    0,
                    sourceY,
                    canvas.width,
                    sliceHeightPx,
                    0,
                    0,
                    canvas.width,
                    sliceHeightPx,
                );

                pdf.addImage(
                    sliceCanvas.toDataURL('image/jpeg', this.imageQuality),
                    'JPEG',
                    dimensions.marginLeft,
                    dimensions.marginTop,
                    dimensions.innerWidth,
                    sliceHeightPx * pxToMm,
                );

                sourceY += sliceHeightPx;
                isFirstSlice = false;
            }
        }

        this.addPageNumbers(pdf, dimensions);
        return pdf.output('blob');
    }

    addPageNumbers(pdf, dimensions) {
        const pageCount = pdf.internal.getNumberOfPages();

        for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
            pdf.setPage(pageNumber);
            pdf.setFontSize(10);
            pdf.text(
                `${pageNumber} / ${pageCount}`,
                dimensions.pageWidth / 2,
                dimensions.pageHeight - 4,
                { align: 'center' },
            );
        }
    }

    async buildPdfBlob() {
        const accordionState = this.captureAccordionState();
        let clone = null;

        try {
            this.expandAccordions(accordionState);
            clone = this.element.cloneNode(true);
        } finally {
            this.restoreAccordions(accordionState);
        }

        const exportRoot = document.createElement('div');
        exportRoot.className = this.exportRootClass;
        exportRoot.style.position = 'absolute';
        exportRoot.style.left = '-99999px';
        exportRoot.style.top = '0';
        exportRoot.style.width = `${this.pdfViewportWidth}px`;
        exportRoot.style.backgroundColor = '#fff';
        exportRoot.style.padding = '0';
        exportRoot.style.margin = '0';

        clone.classList.add('pdf');
        clone.style.margin = '0';
        clone.style.paddingTop = '0';
        this.hidePdfExcludedElements(clone);
        this.expandAccordionsInClone(clone);
        this.normalizePersonImagesInClone(clone);
        exportRoot.appendChild(clone);
        this.body.appendChild(exportRoot);
        try {
            await this.waitForCloneRender(clone);
            this.buildExportFlow(clone);
            await this.waitForLayout();
            this.paginateExportFlow(clone);
            await this.waitForLayout();
            return await this.renderPdf(clone);
        } catch (error) {
            console.error('PDF export failed:', error);
            throw error;
        } finally {
            if (exportRoot.parentNode === this.body) {
                this.body.removeChild(exportRoot);
            }
        }
    }

    async prepareDownload(force = false) {
        if (PdfDownloader.sharedState.owner !== this) {
            return PdfDownloader.sharedState.pendingPrepare || PdfDownloader.sharedState.cachedPdfBlob;
        }

        if (PdfDownloader.sharedState.preparing) {
            return PdfDownloader.sharedState.pendingPrepare;
        }

        if (PdfDownloader.sharedState.isReady && PdfDownloader.sharedState.cachedPdfBlob && !force) {
            return PdfDownloader.sharedState.cachedPdfBlob;
        }

        this.invalidateCachedPdf();
        PdfDownloader.sharedState.preparing = true;
        this.loading = true;
        PdfDownloader.instances.forEach((instance) => instance.setButtonState('preparing'));

        PdfDownloader.sharedState.pendingPrepare = this.buildPdfBlob()
            .then((blob) => {
                PdfDownloader.sharedState.cachedPdfBlob = blob;
                PdfDownloader.sharedState.cachedPdfUrl = URL.createObjectURL(blob);
                PdfDownloader.sharedState.isReady = true;
                PdfDownloader.instances.forEach((instance) => instance.setButtonState('ready'));
                return blob;
            })
            .finally(() => {
                PdfDownloader.sharedState.preparing = false;
                this.loading = false;
                PdfDownloader.sharedState.pendingPrepare = null;
            });

        return PdfDownloader.sharedState.pendingPrepare;
    }

    triggerDownload() {
        if (!PdfDownloader.sharedState.cachedPdfUrl) {
            return;
        }

        const link = document.createElement('a');
        link.href = PdfDownloader.sharedState.cachedPdfUrl;
        link.download = this.pdfFilename;
        this.body.appendChild(link);
        link.click();
        this.body.removeChild(link);
    }

    async download(event) {
        event.preventDefault();

        if (!PdfDownloader.sharedState.isReady || !PdfDownloader.sharedState.cachedPdfUrl) {
            return;
        }

        this.triggerDownload();
    }
}
